import React from 'react';
import { OrbitControls, PerspectiveCamera, useGLTF } from '@react-three/drei';
import { RackWithEcab } from '../modelsFIles/RackWithEcab';

/**
 * Module3D Component
 * 
 * This component is responsible for rendering a 3D module at a specified position using a GLTF model.
 * It clones the scene from the model to ensure multiple instances can be rendered.
 * 
 * @param {Object} props - Component properties.
 * @param {Array} props.position - The position [x, y, z] to place the 3D model.
 * @param {string} props.model - The path to the GLTF model file.
 * 
 * @returns {JSX.Element|null} - A 3D primitive object or null if no valid model or scene is provided.
 */
const Module3D = React.memo(({ position, model }) => {
    const { scene } = useGLTF(model ? model : '/models/placeholder.glb', true);

    // If no valid scene is loaded, don't render anything
    if (!model || !scene) {
        console.warn('Model path is invalid or undefined');
        return null;
    }

    return (
        <primitive
            object={scene.clone()} // Clone the scene to ensure multiple instances
            position={position}
            scale={0.1}
            rotation={[0, 0, 0]}
            dispose={null} // Ensure proper cleanup
        />
    );
});

/**
 * Rack3DView Component
 * 
 * This component displays the 3D rack and modules selected by the user.
 * It uses the PerspectiveCamera and OrbitControls to navigate and view the 3D scene.
 * Selected modules and their submodules are rendered using the Module3D component.
 * 
 * @param {Object} props - Component properties.
 * @param {Array} props.selectedModules - The list of selected modules containing position and 3D file data.
 * 
 * @returns {JSX.Element} - A 3D rack scene with modules and lighting.
 */
const Rack3DView = ({ selectedModules }) => {
    return (
        <>
            {/* Perspective camera set at a specific position */}
            <PerspectiveCamera makeDefault position={[0, 150, 250]} />

            {/* Orbit controls for interacting with the 3D view */}
            <OrbitControls target={[0, 50, 0]} maxPolarAngle={Math.PI / 2 - 0.001} />

            {/* Loop through selected modules and submodules to render their 3D models */}
            {selectedModules.map((module, index) => (
                <React.Fragment key={index}>
                    {/* Render submodules if available */}
                    {module?.subModules?.map((subModule, index1) => (
                        subModule['3DFile'] ? (
                            <Module3D
                                key={index1}
                                position={[subModule.xPos, subModule.yPos, -0.15]} // Position submodules in the rack
                                model={subModule['3DFile']} // 3D model file for the submodule
                            />
                        ) : null
                    ))}

                    {/* Render the main module if it has a 3D file */}
                    {module['3DFile'] ? (
                        <Module3D
                            key={index}
                            position={[0, module.yPos, 0]} // Position main module in the rack
                            model={module['3DFile']} // 3D model file for the main module
                        />
                    ) : null}
                </React.Fragment>
            ))}

            {/* Render the rack model */}
            <mesh>
                <RackWithEcab />
            </mesh>

            {/* Directional lighting for the 3D scene */}
            <directionalLight args={['#ffffff', 1]} /> {/* Main directional light */}
            <directionalLight args={['#ffffff', 1]} position={[0, 0, 1]} /> {/* Additional light */}
        </>
    );
};

export default Rack3DView;
